// A bridge between the visualizer workspace and one-shot readers of
// high-frequency visualizer state (today: the AI chat send path).
//
// The workspace playhead advances at tick rate while media plays, so it must
// not flow through the reactive viewing-context channel — every tick would
// re-render reactive consumers. Instead, the workspace registers a resolver
// here and readers invoke it imperatively at the moment they need a value
// (e.g. when a chat message is sent). The chat send path merges the resolved
// record into `visualizer_state` (see `getContextSnapshot` in
// `useClientViewingContext.ts`).
//
// Adding a new live field is a two-step change, both in the producer's
// domain: contribute the key from the workspace resolver, and list it in
// VOLATILE_VISUALIZER_STATE_FIELDS below so transcript consumers know to
// ignore it when comparing visualizer state across turns.

import { ErrorMonitoringService } from "../ErrorMonitoringService";

/**
 * Wire-format key for the workspace playhead inside `visualizer_state`: the
 * current seek position as a nanosecond decimal string.
 */
export const CURRENT_TIME_NS_FIELD = "current_time_ns";

/**
 * `visualizer_state` keys that are expected to differ between consecutive
 * chat sends (every key a live resolver contributes belongs here). Consumers
 * that compare visualizer state across turns — e.g. the transcript's
 * context-shift detection — must strip these keys first, or a live value
 * like the playhead would register as a context change on every turn.
 */
export const VOLATILE_VISUALIZER_STATE_FIELDS: ReadonlySet<string> = new Set([
  CURRENT_TIME_NS_FIELD,
]);

/**
 * Resolves the producer's current live visualizer state. The producer owns
 * the shape: returned keys are merged into `visualizer_state` as-is, and an
 * empty record contributes nothing.
 */
type LiveVisualizerStateResolver = () => Record<string, unknown>;

let liveResolver: LiveVisualizerStateResolver | undefined;

/**
 * Register the resolver for live visualizer state. Single slot: a later
 * registration replaces an earlier one (only one workspace is mounted at a
 * time). Returns a cleanup function that unregisters — but only if this
 * registration is still the active one. React always runs an unmounting
 * component's cleanup before a remounting component's setup, so the guard is
 * never exercised by remounts or StrictMode; it is defense-in-depth for the
 * hypothetical case of two registrants with overlapping lifetimes, where a
 * stale cleanup must not clear the newer registration.
 */
export function registerLiveVisualizerState(
  resolve: LiveVisualizerStateResolver,
): () => void {
  liveResolver = resolve;
  return () => {
    if (liveResolver === resolve) {
      liveResolver = undefined;
    }
  };
}

/**
 * Resolve the current live visualizer state, or `undefined` when no
 * workspace is registered. A throwing resolver is reported to
 * `ErrorMonitoringService` and treated as `undefined` — callers on the chat
 * send path must never fail because this value was unavailable.
 */
export function getLiveVisualizerState(): Record<string, unknown> | undefined {
  if (liveResolver === undefined) {
    return undefined;
  }
  try {
    return liveResolver();
  } catch (error) {
    ErrorMonitoringService.captureError(error);
    return undefined;
  }
}
